

import { createSlice } from '@reduxjs/toolkit';

export const palette = [
    '#396ab1', '#da7c30', '#3e9651', '#cc2529',
    '#535154', '#6b4c9a', '#922428', '#948b3d',
    '#b6b', '#96b', '#66b', '#36b',
    '#b3b', '#93b', '#63b', '#33b',
    '#bb6', '#9b6', '#6b6', '#3b6',
    '#b96', '#996', '#696', '#396',
    '#b66', '#966', '#666', '#366',
    '#b36', '#936', '#636', '#336',
    '#bb0', '#9b0', '#6b0', '#3b0',
    '#b90', '#990', '#690', '#390',
    '#b60', '#960', '#660', '#360',
    '#b30', '#930', '#630', '#330',
];

// Each entry: { color: "#hex", manual: bool, bold: bool, shade: number }
// - manual: true means the user explicitly picked this color (from the
//   alias popup), as opposed to it being auto-allocated. The allocator
//   never touches an existing entry either way — this flag only matters
//   when working out which of a device's fields anchors its "shade 0"
//   base hue for the rest of its fields (see allocate.js).
// - bold: user-set "double thick" line flag. Independent of color/manual.
// - shade: only meaningful for auto (manual: false) entries. 0 = the
//   device's true base hue; 1, 2, 3... are progressively darker variants
//   used when a device has more than one field on the same chart.
const initialState = {
    allocated: {}
};

// Two generations of damage can arrive from the persisted blob:
//   1. LEGACY STRINGS — pre-refactor blobs stored each entry as a plain
//      "#hex" string. Downstream code reads entry.color (undefined -> gray
//      legend), and entry.bold hits String.prototype.bold — a built-in
//      FUNCTION, truthy — so every legacy series drew double thick.
//   2. SPREAD-STRING CORPSES — the old setBold/setManualColor reducers did
//      { ...entry, ... } while the entry was still one of those strings,
//      scattering it into indexed characters: {0:'#',1:'d',...,bold:false}.
//      Those got persisted. They're objects with NO .color, so the legend
//      goes gray, the popup swatch shows black, and the canvas silently
//      ignores the invalid strokeStyle (lines fall to default black).
// This rebuilds a clean { color, manual, bold, shade } from either form.
// Returns undefined for anything unsalvageable — the caller drops the key
// so the allocator hands it a fresh color on the next data pass.
function normalizeEntry(entry) {
    if (typeof entry === 'string') entry = { color: entry, shade: 0 };
    if (!entry || typeof entry !== 'object') return undefined;

    let color = (typeof entry.color === 'string') ? entry.color : '';
    if (!color) {
        // Reassemble a spread-string corpse from its indexed characters.
        const chars = [];
        for (let i = 0; typeof entry[i] === 'string'; i++) chars.push(entry[i]);
        color = chars.join('');
    }
    if (!/^#[0-9a-f]{3,8}$/i.test(color)) return undefined;

    const out = {
        color,
        manual: entry.manual === true,
        bold:   entry.bold === true,
        shade:  (typeof entry.shade === 'number') ? entry.shade : 0,
    };
    // The auto-allocated original, remembered when the user first overrides
    // a color manually — this is what "Clear" restores.
    if (typeof entry.autoColor === 'string' && /^#[0-9a-f]{3,8}$/i.test(entry.autoColor)) {
        out.autoColor = entry.autoColor;
        out.autoShade = (typeof entry.autoShade === 'number') ? entry.autoShade : 0;
    }
    return out;
}

function normalizeAll(obj) {
    const out = {};
    for (const key in (obj || {})) {
        const entry = normalizeEntry(obj[key]);
        if (entry) out[key] = entry;
    }
    return out;
}

const colorsSlice = createSlice({
    name: 'colors',
    initialState,
    reducers: {
        setColors: (state, action) => {
            state.allocated = normalizeAll(action.payload);
        },
        // Fills in colors for series that don't have one yet (see
        // allocate.js). Never overwrites an existing entry, auto or manual.
        addColors: (state, action) => {
            Object.assign(state.allocated, normalizeAll(action.payload));
        },
        // User picked a color explicitly, from the alias popup. The first
        // time an auto-allocated entry is overridden, its original color and
        // shade are stashed in autoColor/autoShade so "Clear" can restore
        // the exact default later.
        setManualColor: (state, action) => {
            const { seriesKey, color } = action.payload;
            const prev = normalizeEntry(state.allocated[seriesKey]) || {};
            const next = { ...prev, color, manual: true };
            if (!prev.manual && typeof prev.color === 'string' && next.autoColor === undefined) {
                next.autoColor = prev.color;
                next.autoShade = prev.shade ?? 0;
            }
            state.allocated[seriesKey] = next;
        },
        // User toggled "double thick" for a series. Independent of color —
        // doesn't affect how future sibling shades get derived.
        setBold: (state, action) => {
            const { seriesKey, bold } = action.payload;
            if (!state.allocated[seriesKey]) return;
            state.allocated[seriesKey] = {
                ...state.allocated[seriesKey],
                bold,
            };
        },
        // "Clear" in the popup: back to the defaults for this series — the
        // auto-allocated color and normal thickness. If the manual entry
        // predates autoColor bookkeeping (no original recorded), the entry
        // is dropped entirely so the allocator hands it a fresh auto color.
        clearManualStyle: (state, action) => {
            const { seriesKey } = action.payload;
            const entry = state.allocated[seriesKey];
            if (!entry) return;
            if (typeof entry.autoColor === 'string') {
                state.allocated[seriesKey] = {
                    color:  entry.autoColor,
                    manual: false,
                    bold:   false,
                    shade:  (typeof entry.autoShade === 'number') ? entry.autoShade : 0,
                };
            } else if (entry.manual) {
                delete state.allocated[seriesKey];
            } else {
                state.allocated[seriesKey] = { ...entry, bold: false };
            }
        },
        // Puts back the snapshot taken when the popup opened — used to undo
        // live previews when the popup closes without Apply. entry may be
        // undefined (series had no entry yet), in which case the key is
        // removed again.
        restoreEntry: (state, action) => {
            const { seriesKey, entry } = action.payload;
            if (entry) state.allocated[seriesKey] = entry;
            else delete state.allocated[seriesKey];
        },
        // Wipes ALL color history — auto-allocated and manual picks alike —
        // so every series gets re-allocated fresh, starting from
        // palette[0] again, the next time data arrives.
        resetColors: (state) => {
            state.allocated = {};
        }
    }
});

export const { setColors, addColors, setManualColor, setBold, clearManualStyle, restoreEntry, resetColors } = colorsSlice.actions;
export { normalizeAll as normalizeColors };
export default colorsSlice.reducer;